import { addDimensions } from '@eventbrite/site-analytics';
import { $FixMe } from '@eventbrite/ts-utils';
import {
    getSplitNames,
    getTreatments,
    initSplitSdk,
} from '@splitsoftware/splitio-redux';
import { Dispatch } from 'redux';
import {
    ENABLE_CHECKOUT_CORE_RESILIENCY_TLZ,
    RESILIENCE_TLZ_ACTIONS,
} from '../constants/splitio';
import {
    getActiveFFsFromCore,
    getIsAppRebrandActive,
    isAndroidApp as getIsAndroidApp,
    isEventbriteTLD as getIsEventbriteTLD,
    isIosApp as getIsIosApp,
} from '../selectors/app';
import {
    getEventCountryCode,
    getEventOrganizationId,
    getHasPromoCode,
    getIsReservedSeating,
} from '../selectors/event';
import { isSplitTreatmentEnabled } from '../selectors/splitio';
import { getHasTicketsWithPromoCodeApplied } from '../selectors/tickets';
import { CheckoutState } from '../state';
import { getNativeAppVersion } from '../utils/browser';
import {
    CHECKOUT_CORE_RESILIENCY_ACTIONS,
    setTreatments,
} from '../utils/resiliencyFeatureFlag';
import {
    findArrayAttributesTreatment,
    getSplitioClient,
} from '../utils/splitio';
import { saveSplitIOMultiValueTargettingRulesAction } from './splitioMultiValueTargettingRules';

export const initializeSplitioSdk =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        const {
            app: {
                isProd,
                splitIOProdKey,
                event: { id: eventId },
            },
        } = getState();

        const countryCode = getEventCountryCode(getState());
        const hasPromoCode = getHasPromoCode(getState());
        const hasTicketsWithPromoCodeApplied =
            getHasTicketsWithPromoCodeApplied(getState());
        const isAppRebrandActive = getIsAppRebrandActive(getState());
        const isAndroidApp = getIsAndroidApp(getState());
        const isEventbriteTLD = getIsEventbriteTLD(getState());
        const isIosApp = getIsIosApp(getState());
        const isReservedSeating = getIsReservedSeating(getState());
        const nativeAppVersion = getNativeAppVersion();
        const organizationId = getEventOrganizationId(getState());

        // Don't initialize SDK if there is no prod key (eg. unit tests)
        if (!splitIOProdKey) return;

        await dispatch(
            initSplitSdk({
                config: getSplitioClient(
                    eventId,
                    isProd ? splitIOProdKey : null,
                ),
            }) as $FixMe,
        );

        const splitNames = getSplitNames();

        // Apply attributes to evaluate splits
        dispatch(
            getTreatments({
                splitNames: splitNames,
                attributes: {
                    countryCode,
                    eventId,
                    hasPromoCode,
                    hasTicketsWithPromoCodeApplied,
                    isAndroidApp,
                    isAppRebrandActive,
                    isEventbriteTLD,
                    isIosApp,
                    isReservedSeating,
                    nativeAppVersion,
                    organizationId,
                },
            }) as any,
        );

        // Send split values to analytics
        const state = getState();
        const activeFFsFromCore = getActiveFFsFromCore(state);
        const activeSplits = splitNames.flatMap((name) => {
            const value = isSplitTreatmentEnabled(state, name);
            return value ? [name] : [];
        });

        // Gather multivalue attribute targetting rules
        const enableCheckoutCoreResiliencyTlzTreatments =
            await findArrayAttributesTreatment({
                featureFlag: ENABLE_CHECKOUT_CORE_RESILIENCY_TLZ,
                // TODO: Refactor to use only actions from CHECKOUT_CORE_RESILIENCY_ACTIONS
                attributes: Array.from(
                    new Set([
                        ...CHECKOUT_CORE_RESILIENCY_ACTIONS,
                        ...Object.keys(RESILIENCE_TLZ_ACTIONS),
                    ]),
                ),
                attributeName: 'actionName',
                getTreatments,
                extraAttributes: {
                    countryCode,
                    eventId,
                    hasPromoCode,
                    hasTicketsWithPromoCodeApplied,
                    isAndroidApp,
                    isAppRebrandActive,
                    isEventbriteTLD,
                    isIosApp,
                    isReservedSeating,
                    nativeAppVersion,
                    organizationId,
                },
            });

        setTreatments(enableCheckoutCoreResiliencyTlzTreatments);

        dispatch(
            saveSplitIOMultiValueTargettingRulesAction({
                [ENABLE_CHECKOUT_CORE_RESILIENCY_TLZ]:
                    enableCheckoutCoreResiliencyTlzTreatments,
            }),
        );

        addDimensions({
            experimentId: [...activeSplits, ...activeFFsFromCore]
                .sort()
                .join(','),
        });

        return Promise.resolve();
    };
